import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useApp } from "../context/AppContext";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, authFetch } from "../utils/api";
import { compressImage } from "../utils/imageCompress";
import QRCode from "qrcode";
import { formatScore } from "../utils/format";
import SponsorModal from "../components/SponsorModal";
import CreditPointsModal from "../components/CreditPointsModal";
import BookingModal from "../components/BookingModal";
import ServiceBookingModal from "../components/ServiceBookingModal";
import VerifiedBadge from "../components/VerifiedBadge";
import PageMeta from "../components/PageMeta";
import { usePaymentReturnBanner } from "../utils/paymentReturn";
import VenueVibesViewer from "../components/VenueVibesViewer";
import { Avatar, vibeRingKind } from "../components/Avatar";
import { CrownBadge, VenueTier, TIER_COLORS, TIER_LABELS } from "../components/CrownBadge";
import { t } from "../utils/i18n";
import { useIsMobile } from "../hooks/useIsMobile";

const initials = (name?: string) => (name || "?").split(" ").map(p => p[0]).join("").slice(0, 2).toUpperCase();

// Même liste que components/CreateVenueModal.tsx (redéfinie ici plutôt qu'importée : deux petits
// tableaux, pas besoin d'un couplage inter-fichiers pour ça). Éditable ici en plus de la
// création : un lieu créé "Lieu" doit pouvoir évoluer vers Pro/Association après coup.
const VENUE_SUBTYPES: { key: string; label: string; sub: string }[] = [
  { key: "lieu", label: "Lieu", sub: "Comme aujourd'hui" },
  { key: "pro", label: "Pro", sub: "Société, indépendant, groupe" },
  { key: "association", label: "Association", sub: "" },
];
const JOIN_POLICIES: { key: string; label: string }[] = [
  { key: "approval", label: "Sur demande (vous validez chaque adhésion)" },
  { key: "open", label: "Ouvert (adhésion immédiate)" },
  { key: "invite_only", label: "Sur invitation uniquement" },
];

const DAYS_SHORT = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

const PhoneIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);
const GlobeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/>
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
  </svg>
);
const DirectionsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="3 11 22 2 13 21 11 13 3 11"/>
  </svg>
);
const CalendarIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);
const BriefcaseIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/><line x1="2" y1="13" x2="22" y2="13"/>
  </svg>
);
const StarIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
  </svg>
);

const RATING_CRITERIA = [
  { key: "ponctualite", label: "Ponctualité" },
  { key: "respect", label: "Respect & courtoisie" },
  { key: "ambiance", label: "Bonne humeur" },
  { key: "fiabilite", label: "Fiabilité" },
  { key: "recommandation", label: "Recommandation" },
];
const PALETTE = ["#0a0a0a", "#0a0a0a", "#0a0a0a", "#333333", "#666666", "#777777", "#4d4d4d"];
const colorFromId = (id: string) => {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = id.charCodeAt(i) + ((hash << 5) - hash);
  return PALETTE[Math.abs(hash) % PALETTE.length];
};

export default function Venue() {
  const { user, lang } = useApp();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  usePaymentReturnBanner();
  const [activeTab, setActiveTab] = useState("events");
  const [buyingItemId, setBuyingItemId] = useState<string | null>(null);

  const [venue, setVenue] = useState<any>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [reviews, setReviews] = useState<any[]>([]);
  const [photos, setPhotos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editName, setEditName] = useState("");
  const [editType, setEditType] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editWebsite, setEditWebsite] = useState("");
  const [editSubtype, setEditSubtype] = useState("lieu");
  const [editJoinPolicy, setEditJoinPolicy] = useState("approval");
  const [savingEdit, setSavingEdit] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [myRating, setMyRating] = useState(0);
  const [myComment, setMyComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [showClaimModal, setShowClaimModal] = useState(false);
  // Arrivée depuis la fiche publique SEO ("C'est votre lieu ?", voir routes/publicPages.ts) via
  // /login ou /register?redirect=... — ouvre directement le modal plutôt que de faire rechercher
  // le bouton à quelqu'un qui vient d'exprimer cette intention avant même de se connecter.
  useEffect(() => {
    if (searchParams.get("claim") === "1") setShowClaimModal(true);
  }, [searchParams]);
  const [claimMessage, setClaimMessage] = useState("");
  const [submittingClaim, setSubmittingClaim] = useState(false);
  const [claimSent, setClaimSent] = useState(false);

  const [rateableEvents, setRateableEvents] = useState<any[]>([]);
  const [loadingRateable, setLoadingRateable] = useState(false);
  const [ratingTarget, setRatingTarget] = useState<{ eventId: string; eventTitle: string; client: any } | null>(null);
  const [ratingValues, setRatingValues] = useState<Record<string, number>>({});
  const [submittingRating, setSubmittingRating] = useState(false);
  // Catégorie de Power ciblée par l'agenda/la réservation — "" = espace générique du lieu
  // (comportement inchangé), une valeur = agenda scopé à ce Power précis (ex: "Ménage").
  const [selectedCategory, setSelectedCategory] = useState("");
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [showCreditPointsModal, setShowCreditPointsModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showServiceBookingModal, setShowServiceBookingModal] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [editHours, setEditHours] = useState<any>({ mode: "scheduled", schedule: [], closures: [] });
  // Abonnés (compteur + liste) et Souvenirs (mes propres photos/lives à ce lieu) : deux sections
  // fixes affichées avant les onglets, jamais l'une des tabs elles-mêmes — voir le commentaire
  // au-dessus de leur rendu JSX pour l'ordre exact (Souvenirs passe devant si non vide).
  const [followers, setFollowers] = useState<any[]>([]);
  const [memories, setMemories] = useState<any[]>([]);
  const [followLoading, setFollowLoading] = useState(false);
  // Membres — réservé aux lieux Pro/Association (venue.subtype !== 'lieu'), système distinct du
  // Follow ci-dessus : adhésion réelle (sur demande/invitation selon join_policy), rôles
  // admin/staff/member. Même structure que Community.tsx (members/changeMemberRole/removeMember).
  const [venueMembers, setVenueMembers] = useState<any[]>([]);
  const [pendingMembers, setPendingMembers] = useState<any[]>([]);
  const [memberActionLoading, setMemberActionLoading] = useState(false);
  // Inviter des membres — depuis les amis de l'admin/staff qui invite, même endpoint POST
  // /:id/members/invite que la Phase 1 (backend déjà prêt, l'UI manquait).
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteFriends, setInviteFriends] = useState<any[]>([]);
  const [loadingInviteFriends, setLoadingInviteFriends] = useState(false);
  const [invitedIds, setInvitedIds] = useState<Set<string>>(new Set());
  // Check-in QR — le lieu AFFICHE le jeton (jamais l'inverse, voir routes/attendance.ts), rafraîchi
  // avant l'expiration (jeton valable 45s côté serveur) pour qu'un écran laissé ouvert à l'entrée
  // reste toujours scannable.
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [checkinSvg, setCheckinSvg] = useState<string | null>(null);
  // Gamification (Phase 3) — carte de fidélité du membre courant (points/niveau/badges obtenus
  // + les badges du lieu pas encore débloqués, pour montrer la progression).
  const [myEngagement, setMyEngagement] = useState<any>(null);
  const [myBadges, setMyBadges] = useState<any[]>([]);
  // Palier Bronze/Silver/Gold (3 visites / 5 visites+1 événement / 3 événements), calculé côté
  // serveur (utils/venueTiers.ts) — remplace l'ancien système à 5 badges nommés.
  // Tableau de bord de gestion (Phase 4) — couche de lecture pure sur des données déjà
  // existantes (participants/paiements/liste d'attente) recoupées avec la présence de la Phase 2.
  const [venueEvents, setVenueEvents] = useState<any[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [eventOps, setEventOps] = useState<any>(null);
  const [loadingOps, setLoadingOps] = useState(false);
  // Anneau bleu/rouge sur la photo carrée (venue.has_vibe_content / has_real_content, calculés
  // côté serveur) : au tap, ce qu'il s'y passe VRAIMENT — vibes/lives/reals tagués à ce lieu,
  // directement ou via l'activité qu'ils racontent (GET /venues/:id/vibes).
  const [vibesViewerOpen, setVibesViewerOpen] = useState(false);
  const [vibesItems, setVibesItems] = useState<any[]>([]);
  const [loadingVibes, setLoadingVibes] = useState(false);
  const openVibesViewer = () => {
    if (!id) return;
    setVibesViewerOpen(true);
    setLoadingVibes(true);
    api.get(`/venues/${id}/vibes`).then(data => setVibesItems(data?.items || [])).catch(() => setVibesItems([])).finally(() => setLoadingVibes(false));
  };

  useEffect(() => {
    if (!id) return;
    api.get(`/venues/${id}`).then(data => {
      if (!data?.venue) { setNotFound(true); return; }
      setVenue(data.venue);
      setEvents(data.events || []);
      setReviews(data.reviews || []);
      setPhotos(data.photos || []);
      const mine = (data.reviews || []).find((r: any) => r.user_id === user?.id);
      if (mine) { setMyRating(Number(mine.rating)); setMyComment(mine.comment || ""); }
    }).catch(() => setNotFound(true)).finally(() => setLoading(false));
    api.get(`/venues/${id}/followers`).then(data => setFollowers(data?.followers || [])).catch(() => {});
    api.get(`/venues/${id}/memories`).then(data => setMemories(data?.memories || [])).catch(() => {});
  }, [id]);

  // Suivre / ne plus suivre : optimiste sur venue.followed_by_me / followers_count, comme le
  // reste de la page gère déjà venue en un seul objet d'état plutôt que des champs séparés.
  const toggleFollow = async () => {
    if (!id || !venue || !user || followLoading) return;
    // Le clic était trop souvent involontaire (aucune confirmation avant un vrai "je ne suis
    // plus intéressé") — on ne demande rien pour suivre, seulement avant de se désabonner.
    if (venue.followed_by_me && !window.confirm(`${t(lang, "venue_unfollow_confirm_prefix")} ${venue.name} ?`)) return;
    setFollowLoading(true);
    const wasFollowing = !!venue.followed_by_me;
    setVenue((v: any) => ({ ...v, followed_by_me: !wasFollowing, followers_count: Math.max(0, Number(v.followers_count || 0) + (wasFollowing ? -1 : 1)) }));
    setFollowers(prev => wasFollowing ? prev.filter(f => f.id !== user.id) : [{ id: user.id, name: user.name, avatar_url: user.avatar_url, is_verified: user.is_verified }, ...prev]);
    try {
      if (wasFollowing) await api.delete(`/venues/${id}/follow`);
      else await api.post(`/venues/${id}/follow`, {});
    } catch {
      setVenue((v: any) => ({ ...v, followed_by_me: wasFollowing, followers_count: Math.max(0, Number(v.followers_count || 0) + (wasFollowing ? 1 : -1)) }));
      setFollowers(prev => wasFollowing ? [{ id: user.id, name: user.name, avatar_url: user.avatar_url, is_verified: user.is_verified }, ...prev] : prev.filter(f => f.id !== user.id));
    } finally {
      setFollowLoading(false);
    }
  };

  // Membres — chargés seulement à l'ouverture de l'onglet (comme "clients" ci-dessous), pas
  // besoin de les avoir en mémoire pour le reste de la page.
  const loadVenueMembers = () => {
    if (!id) return;
    const isStaff = venue?.my_member_role === "admin" || venue?.my_member_role === "staff";
    api.get(`/venues/${id}/members${isStaff ? "" : "?status=active"}`).then(data => {
      const all = data?.members || [];
      setVenueMembers(all.filter((m: any) => m.status === "active"));
      setPendingMembers(all.filter((m: any) => m.status === "pending"));
    }).catch(() => {});
  };
  useEffect(() => {
    if (!id || activeTab !== "members" || !venue || venue.subtype === "lieu") return;
    loadVenueMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, activeTab, venue?.subtype]);

  // Sa propre carte de fidélité — ouverte à tout type de lieu et tout abonné (pas seulement un
  // membre formel d'un lieu Pro/Association, voir GET /:id/members/:userId/engagement) : chargée
  // dès qu'on ouvre l'onglet Catalogue ou Membres, pour savoir si on a assez de points pour un
  // article payable en points ("Acheter avec mes points").
  useEffect(() => {
    if ((activeTab !== "members" && activeTab !== "catalogue") || !id || !user?.id || myEngagement) return;
    api.get(`/venues/${id}/members/${user.id}/engagement`).then(data => {
      setMyEngagement(data?.engagement || null);
      setMyBadges(data?.badges || []);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, activeTab, user?.id, myEngagement]);

  useEffect(() => {
    if (!id || activeTab !== "ops") return;
    api.get(`/venues/${id}/events`).then(data => setVenueEvents(data?.events || [])).catch(() => {});
  }, [id, activeTab]);

  // Pro Events — jamais public (voir GET /venues/:id/pro-events) : abonnés/membres/gestionnaire
  // de CE lieu, ou tout admin/staff d'un AUTRE lieu Pro/Association (réseau entre lieux Pro).
  // proEventsRestricted distingue "vide" (liste chargée, 0 résultat) de "pas le droit de voir" (403).
  const [proEvents, setProEvents] = useState<any[] | null>(null);
  const [proEventsRestricted, setProEventsRestricted] = useState(false);
  useEffect(() => {
    if (!id || activeTab !== "pro_events" || !venue || venue.subtype === "lieu" || proEvents !== null) return;
    api.get(`/venues/${id}/pro-events`)
      .then(data => { setProEvents(data?.events || []); setProEventsRestricted(false); })
      .catch(() => { setProEvents([]); setProEventsRestricted(true); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, activeTab, venue?.subtype, proEvents]);

  useEffect(() => {
    if (!id || !selectedEventId) { setEventOps(null); return; }
    setLoadingOps(true);
    api.get(`/venues/${id}/events/${selectedEventId}/ops`).then(data => setEventOps(data)).catch(() => setEventOps(null)).finally(() => setLoadingOps(false));
  }, [id, selectedEventId]);

  // Rafraîchi toutes les 30s (jeton valable 45s) pendant que le panneau est ouvert — s'arrête dès
  // qu'on referme, jamais de polling en arrière-plan inutile.
  useEffect(() => {
    if (!checkinOpen || !id) return;
    let cancelled = false;
    const fetchToken = () => {
      api.post(`/venues/${id}/checkin-token`, {}).then(async data => {
        if (cancelled || !data?.token) return;
        const svg = await QRCode.toString(data.token, { type: "svg", margin: 1, width: 200 }).catch(() => null);
        if (!cancelled) setCheckinSvg(svg);
      }).catch(() => {});
    };
    fetchToken();
    const interval = setInterval(fetchToken, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [checkinOpen, id]);

  const openInviteModal = () => {
    setShowInviteModal(true);
    setInvitedIds(new Set());
    if (inviteFriends.length === 0 && user?.id) {
      setLoadingInviteFriends(true);
      api.get(`/users/${user.id}/friends`).then(data => setInviteFriends(data?.friends || [])).catch(() => {}).finally(() => setLoadingInviteFriends(false));
    }
  };
  const inviteFriend = (friendId: string) => {
    if (!id || invitedIds.has(friendId)) return;
    setInvitedIds(prev => new Set(prev).add(friendId));
    api.post(`/venues/${id}/members/invite`, { user_id: friendId }).then(() => loadVenueMembers()).catch(() => {
      setInvitedIds(prev => { const next = new Set(prev); next.delete(friendId); return next; });
    });
  };

  const refreshMyMembership = () => {
    if (!id) return;
    api.get(`/venues/${id}`).then(data => { if (data?.venue) setVenue(data.venue); }).catch(() => {});
  };

  const joinVenueMembers = () => {
    if (!id || memberActionLoading) return;
    setMemberActionLoading(true);
    api.post(`/venues/${id}/members/join`, {}).then(() => { refreshMyMembership(); loadVenueMembers(); }).catch(() => {}).finally(() => setMemberActionLoading(false));
  };
  const leaveVenueMembers = () => {
    if (!id || memberActionLoading) return;
    if (!window.confirm(t(lang, "venue_leave_confirm_title"))) return;
    setMemberActionLoading(true);
    api.post(`/venues/${id}/members/leave`, {}).then(data => {
      if (data?.error) { alert(data.error); return; }
      refreshMyMembership(); loadVenueMembers();
    }).catch(() => {}).finally(() => setMemberActionLoading(false));
  };
  const approveVenueMember = (userId: string) => {
    if (!id) return;
    api.put(`/venues/${id}/members/${userId}/approve`, {}).then(() => loadVenueMembers()).catch(() => {});
  };
  const rejectVenueMember = (userId: string) => {
    if (!id) return;
    api.put(`/venues/${id}/members/${userId}/reject`, {}).then(() => loadVenueMembers()).catch(() => {});
  };
  const changeVenueMemberRole = (userId: string, role: string) => {
    if (!id) return;
    api.put(`/venues/${id}/members/${userId}/role`, { role })
      .then(data => { if (data?.error) { alert(data.error); return; } setVenueMembers(prev => prev.map(m => m.user_id === userId ? { ...m, role } : m)); })
      .catch(() => alert("Erreur de connexion"));
  };
  const removeVenueMember = (userId: string, name: string) => {
    if (!id) return;
    if (!window.confirm(`Exclure ${name} des membres ?`)) return;
    api.delete(`/venues/${id}/members/${userId}`).then(() => loadVenueMembers()).catch(() => {});
  };

  useEffect(() => {
    if (!id || activeTab !== "clients" || !venue || venue.owner_id !== user?.id) return;
    setLoadingRateable(true);
    api.get(`/venues/${id}/rateable-clients`).then(data => setRateableEvents(data?.events || [])).catch(() => {}).finally(() => setLoadingRateable(false));
  }, [id, activeTab, venue, user?.id]);

  const submitRating = () => {
    if (!ratingTarget || !id) return;
    const allRated = RATING_CRITERIA.every(c => ratingValues[c.key] > 0);
    if (!allRated) return;
    setSubmittingRating(true);
    api.post("/ratings", {
      rated_id: ratingTarget.client.id,
      publication_id: ratingTarget.eventId === "reviews" ? undefined : ratingTarget.eventId,
      venue_id: id,
      ...ratingValues,
    }).then(data => {
      if (!data?.error) {
        setRateableEvents(prev => prev.map(ev => ev.id !== ratingTarget.eventId ? ev : {
          ...ev,
          participants: ev.participants.map((p: any) => p.id === ratingTarget.client.id ? { ...p, already_rated: true } : p),
        }));
        setRatingTarget(null);
        setRatingValues({});
      }
    }).finally(() => setSubmittingRating(false));
  };

  const submitReview = () => {
    if (!id || myRating === 0) return;
    setSubmittingReview(true);
    setReviewError("");
    api.post(`/venues/${id}/reviews`, { rating: myRating, comment: myComment || null }).then(data => {
      if (data?.review) {
        setReviews(prev => [data.review, ...prev.filter((r: any) => r.user_id !== data.review.user_id)]);
      } else if (data?.error) {
        setReviewError(data.error);
      }
    }).catch(() => setReviewError("Erreur de connexion")).finally(() => setSubmittingReview(false));
  };

  const myReview = reviews.find((r: any) => r.user_id === user?.id);
  const REVIEW_EDIT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
  const myReviewEditable = !myReview || (Date.now() - new Date(myReview.created_at).getTime()) < REVIEW_EDIT_WINDOW_MS;

  const participate = (eventId: string) => {
    api.post(`/publications/${eventId}/participate`, {}).catch(() => {});
  };

  const openEditModal = () => {
    setEditName(venue.name || "");
    setEditType(venue.type || "");
    setEditDescription(venue.description || "");
    setEditPhone(venue.phone || "");
    setEditWebsite(venue.website || "");
    setEditHours(venue.hours || { mode: "scheduled", schedule: [], closures: [] });
    setEditSubtype(venue.subtype || "lieu");
    setEditJoinPolicy(venue.join_policy || "approval");
    setShowEditModal(true);
  };

  const saveEdit = () => {
    if (!id) return;
    setSavingEdit(true);
    const body: any = { name: editName, type: editType, description: editDescription, phone: editPhone || null, website: editWebsite || null, hours: editHours };
    if (editSubtype !== venue.subtype) body.subtype = editSubtype;
    if (editSubtype !== "lieu") body.join_policy = editJoinPolicy;
    api.put(`/venues/${id}`, body)
      .then(data => {
        if (data?.venue) { setVenue(data.venue); setShowEditModal(false); }
        else alert(data?.error || "Erreur lors de la mise à jour");
      })
      .catch(() => alert("Erreur de connexion"))
      .finally(() => setSavingEdit(false));
  };

  const dayHours = (dow: number) =>(editHours.schedule || []).find((s: any) => s.day === dow) || { day: dow, open: "09:00", close: "18:00", closed: true };
  const setDayHours = (dow: number, patch: any) => {
    setEditHours((prev: any) => {
      const schedule = [...(prev.schedule || [])];
      const idx = schedule.findIndex((s: any) => s.day === dow);
      const current = idx >= 0 ? schedule[idx] : { day: dow, open: "09:00", close: "18:00", closed: true };
      const updated = { ...current, ...patch };
      if (idx >= 0) schedule[idx] = updated; else schedule.push(updated);
      return { ...prev, schedule };
    });
  };
  const addClosure = () => setEditHours((prev: any) => ({ ...prev, closures: [...(prev.closures || []), { start: "", end: "", reason: "" }] }));
  const updateClosure = (i: number, patch: any) => setEditHours((prev: any) => ({ ...prev, closures: (prev.closures || []).map((c: any, idx: number) => idx === i ? { ...c, ...patch } : c) }));
  const removeClosure = (i: number) => setEditHours((prev: any) => ({ ...prev, closures: (prev.closures || []).filter((_: any, idx: number) => idx !== i) }));

  const uploadPhoto = async (file: File) => {
    if (!id) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", await compressImage(file));
      const uploadRes = await authFetch("/uploads/venue", { method: "POST", body: formData }).then(r => r.json());
      if (uploadRes?.url) {
        const data = await api.post(`/venues/${id}/photos`, { url: uploadRes.url });
        if (data?.photo) setPhotos(prev => [...prev, data.photo]);
      }
    } catch {
      alert("Erreur lors de l'envoi de la photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  const uploadVenueImage = async (file: File, field: "avatar_url" | "cover_url") => {
    if (!id) return;
    const setUploading = field === "avatar_url" ? setUploadingAvatar : setUploadingCover;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", await compressImage(file));
      const uploadRes = await authFetch("/uploads/venue", { method: "POST", body: formData }).then(r => r.json());
      if (uploadRes?.url) {
        const data = await api.put(`/venues/${id}`, { [field]: uploadRes.url });
        if (data?.venue) setVenue(data.venue);
        else alert(data?.error || "Erreur lors de la mise à jour");
      }
    } catch {
      alert("Erreur lors de l'envoi de la photo");
    } finally {
      setUploading(false);
    }
  };

  const deletePhoto = (photoId: string) => {
    if (!id) return;
    api.delete(`/venues/${id}/photos/${photoId}`).then(() => setPhotos(prev => prev.filter(p => p.id !== photoId))).catch(() => {});
  };

  useEffect(() => {
    if (!id || activeTab !== "catalogue") return;
    setLoadingItems(true);
    api.get(`/venues/${id}/items`).then(data => setItems(data?.items || [])).catch(() => {}).finally(() => setLoadingItems(false));
  }, [id, activeTab]);

  const [itemTitle, setItemTitle] = useState("");
  const [itemDescription, setItemDescription] = useState("");
  const [itemPrice, setItemPrice] = useState("");
  const [itemPointsPrice, setItemPointsPrice] = useState("");
  const [itemType, setItemType] = useState<"sale" | "rental">("sale");
  const [itemPhoto, setItemPhoto] = useState("");
  const [uploadingItemPhoto, setUploadingItemPhoto] = useState(false);
  const [savingItem, setSavingItem] = useState(false);

  const openItemModal = (item?: any) => {
    setEditingItem(item || null);
    setItemTitle(item?.title || "");
    setItemDescription(item?.description || "");
    setItemPrice(item?.price != null ? String(item.price) : "");
    setItemPointsPrice(item?.points_price != null ? String(item.points_price) : "");
    setItemType(item?.item_type === "rental" ? "rental" : "sale");
    setItemPhoto(item?.photo_url || "");
    setShowItemModal(true);
  };

  const uploadItemPhoto = async (file: File) => {
    setUploadingItemPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", await compressImage(file));
      const uploadRes = await authFetch("/uploads/venue", { method: "POST", body: formData }).then(r => r.json());
      if (uploadRes?.url) setItemPhoto(uploadRes.url);
    } catch {
      alert("Erreur lors de l'envoi de la photo");
    } finally {
      setUploadingItemPhoto(false);
    }
  };

  const saveItem = () => {
    if (!id || !itemTitle.trim()) return;
    setSavingItem(true);
    const body = {
      title: itemTitle.trim(),
      description: itemDescription.trim() || null,
      price: itemPrice ? Number(itemPrice) : null,
      points_price: itemPointsPrice ? Number(itemPointsPrice) : null,
      item_type: itemType,
      photo_url: itemPhoto || null,
    };
    const req = editingItem ? api.put(`/venues/items/${editingItem.id}`, body) : api.post(`/venues/${id}/items`, body);
    req.then(data => {
      if (data?.item) {
        setItems(prev => editingItem ? prev.map(it => it.id === data.item.id ? data.item : it) : [data.item, ...prev]);
        setShowItemModal(false);
      } else alert(data?.error || "Erreur lors de l'enregistrement");
    }).catch(() => alert("Erreur de connexion")).finally(() => setSavingItem(false));
  };

  const deleteItem = (itemId: string) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    api.delete(`/venues/items/${itemId}`).then(() => setItems(prev => prev.filter(it => it.id !== itemId))).catch(() => {});
  };

  const toggleItemAvailable = (item: any) => {
    api.put(`/venues/items/${item.id}`, { is_available: !item.is_available }).then(data => {
      if (data?.item) setItems(prev => prev.map(it => it.id === data.item.id ? data.item : it));
    }).catch(() => {});
  };

  const submitClaim = () => {
    if (!id) return;
    setSubmittingClaim(true);
    api.post(`/venues/${id}/claim`, { message: claimMessage || null })
      .then(() => { setClaimSent(true); })
      .catch(() => {})
      .finally(() => setSubmittingClaim(false));
  };

  const isOwner = venue?.owner_id === user?.id;
  // Membres — réservé aux lieux Pro/Association, jamais un simple "Lieu" (voir venue.subtype).
  const isVenueMember = venue?.my_member_role === "admin" || venue?.my_member_role === "staff" || venue?.my_member_role === "member";
  const isVenueAdmin = venue?.my_member_role === "admin";
  const isVenueStaff = isVenueAdmin || venue?.my_member_role === "staff";
  const TABS = [
    { key: "events", label: t(lang, "venue_tab_events") },
    { key: "gallery", label: "Galerie" },
    { key: "catalogue", label: t(lang, "venue_tab_catalog") },
    { key: "reviews", label: t(lang, "venue_tab_reviews") },
    { key: "infos", label: t(lang, "venue_tab_infos") },
    ...(isOwner ? [{ key: "clients", label: "Noter" }] : []),
    ...(venue && venue.subtype !== "lieu" ? [{ key: "members", label: t(lang, "venue_tab_members") }] : []),
    ...(venue && venue.subtype !== "lieu" ? [{ key: "pro_events", label: t(lang, "venue_tab_pro_events") }] : []),
    ...(isVenueStaff ? [{ key: "ops", label: "Gestion" }] : []),
  ];

  const card: React.CSSProperties = {
    backgroundColor: "white", borderRadius: 16, padding: 20,
    marginBottom: 12, border: "1px solid #F0F0F0"
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#999" }}>{t(lang, "msg_loading")}</div>;
  if (notFound || !venue) return <div style={{ padding: 40, textAlign: "center", color: "#999" }}>{t(lang, "venue_not_found")}.</div>;

  const rating = venue.average_rating ? Number(venue.average_rating) : 0;
  const reviewsCount = Number(venue.reviews_count || 0);
  const eventsCount = Number(venue.events_count || 0);

  return (
    <>
    <PageMeta title={venue.name || "Lieu"} description={venue.description} image={venue.cover_url} />
    {/* .no-scrollbar est utilisé plus bas (abonnés, onglets) mais n'était jamais défini dans ce
        fichier — il ne fonctionnait que par hasard si une autre page l'avait déjà injecté avant
        la navigation. Défini ici localement, même recette que Explore.tsx/Search.tsx. */}
    <style>{`.no-scrollbar::-webkit-scrollbar{display:none} .no-scrollbar{scrollbar-width:none;-ms-overflow-style:none}`}</style>
    <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", minHeight: "calc(100vh - 60px)", overflow: isMobile ? "visible" : "hidden" }}>
      <div style={{ flex: 1, overflowY: "auto" }}>

        {/* COVER */}
        <div style={{ height: 220, background: venue.cover_url ? `url(${venue.cover_url}) center/cover` : `linear-gradient(135deg, ${colorFromId(venue.id)} 0%, #0a0a0a 100%)`, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {isOwner && (
            <label style={{ position: "absolute", inset: 0, cursor: "pointer" }}>
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) uploadVenueImage(f, "cover_url"); e.target.value = ""; }} />
              <div style={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.3)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.2s" }}
                onMouseOver={e => (e.currentTarget.style.opacity = "1")}
                onMouseOut={e => (e.currentTarget.style.opacity = "0")}>
                <div style={{ backgroundColor: "rgba(0,0,0,0.6)", borderRadius: 20, padding: "8px 16px" }}>
                  <span style={{ color: "white", fontSize: 13, fontWeight: 600 }}>{uploadingCover ? "Envoi..." : "Changer la couverture"}</span>
                </div>
              </div>
            </label>
          )}
          <label
            onClick={!isOwner && (venue.has_vibe_content || venue.has_real_content) ? openVibesViewer : undefined}
            style={{ position: "absolute", bottom: -28, left: 24, cursor: isOwner || venue.has_vibe_content || venue.has_real_content ? "pointer" : "default", zIndex: 2 }}
          >
            {isOwner && <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) uploadVenueImage(f, "avatar_url"); e.target.value = ""; }} />}
            {/* Anneau "ça se passe ici" — rouge (real) prioritaire sur bleu (vibe), un seul à la
                fois, jamais à la place du liseré blanc existant qui, lui, ne dit rien de plus
                qu'"c'est la photo du lieu" (uniformisation demandée explicitement, cf. plan
                Explorer+Vibes). */}
            <div style={{
              padding: (venue.has_real_content || venue.has_vibe_content) ? 5 : 0, borderRadius: 19,
              border: venue.has_real_content ? "3px solid #FF3B30" : venue.has_vibe_content ? "3px solid #3B82F6" : "none",
            }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, backgroundColor: colorFromId(venue.id), backgroundImage: venue.avatar_url ? `url(${venue.avatar_url})` : "none", backgroundSize: "cover", backgroundPosition: "center", border: "3px solid white", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 900, fontSize: 18, boxShadow: "0 4px 12px rgba(0,0,0,0.2)", position: "relative" }}>
                {!venue.avatar_url && initials(venue.name)}
                {isOwner && (
                  <div style={{ position: "absolute", inset: 0, borderRadius: 14, backgroundColor: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", opacity: 0, transition: "opacity 0.2s" }}
                    onMouseOver={e => (e.currentTarget.style.opacity = "1")}
                    onMouseOut={e => (e.currentTarget.style.opacity = "0")}>
                    <span style={{ color: "white", fontSize: 9, fontWeight: 700 }}>{uploadingAvatar ? "..." : "Modifier"}</span>
                  </div>
                )}
              </div>
            </div>
          </label>
          {venue.is_verified && (
            // top décalé sous l'encoche/horloge du téléphone (env(safe-area-inset-top)) : la
            // page a bien un padding de sécurité global (Layout.tsx), mais ce badge est
            // positionné en absolu DANS la couverture, donc indépendant de ce padding. Toujours
            // signalé caché sous l'horloge malgré le safe-area-inset (probablement 0 tant que
            // Safari n'est pas en mode plein écran/barre repliée) : max() garantit maintenant au
            // moins 50px d'espace, peu importe ce que env() retourne.
            <div style={{ position: "absolute", top: "max(50px, calc(16px + env(safe-area-inset-top)))", right: 16, backgroundColor: "rgba(0,0,0,0.5)", borderRadius: 20, padding: "4px 10px" }}>
              <span style={{ color: "white", fontSize: 11, fontWeight: 600 }}>{t(lang, "venue_certified")}</span>
            </div>
          )}
          <div style={{ position: "absolute", bottom: 16, right: 16, display: "flex", gap: 8 }}>
            {[
              { label: eventsCount.toLocaleString(), sub: t(lang, "venue_events_stat_label") },
              { label: reviewsCount.toLocaleString(), sub: t(lang, "venue_reviews_suffix") },
              { label: rating ? formatScore(rating) : "-", sub: "/10" },
            ].map((s, i) => (
              <div key={i} style={{ backgroundColor: "rgba(0,0,0,0.5)", borderRadius: 10, padding: "6px 10px", textAlign: "center" }}>
                <div style={{ color: "white", fontWeight: 700, fontSize: 14 }}>{s.label}</div>
                <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 10 }}>{s.sub}</div>
              </div>
            ))}
          </div>
        </div>

        {/* INFOS PRINCIPALES */}
        <div style={{ padding: "40px 24px 16px" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <h1 style={{ fontSize: 22, fontWeight: 800 }}>{venue.name}</h1>
                {venue.is_verified && <VerifiedBadge size={18} />}
              </div>
              {venue.type && <span style={{ backgroundColor: "#F0F0F0", borderRadius: 20, padding: "3px 10px", fontSize: 12, color: "#666" }}>{venue.type}</span>}
            </div>
          </div>

          {reviewsCount > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <div style={{ backgroundColor: "#0a0a0a", borderRadius: 20, padding: "3px 10px", color: "white", fontSize: 13, fontWeight: 700 }}>{formatScore(rating)}/10</div>
              <span style={{ fontSize: 13, color: "#666" }}>{reviewsCount} {t(lang, "venue_reviews_suffix")}</span>
            </div>
          )}

          {!venue.has_services && venue.provider_categories_effective?.length > 0 && (isOwner || venue.has_availability) && (
            <select
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              style={{ marginBottom: 10, padding: "8px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 13, backgroundColor: "white" }}
            >
              <option value="">{t(lang, "venue_general_space")}</option>
              {venue.provider_categories_effective.map((c: string) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}

          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" as const }}>
            {venue.phone && (
              <a href={`tel:${venue.phone}`} title="Appeler" style={{ width: 44, height: 44, borderRadius: "50%", border: "1px solid #E0E0E0", backgroundColor: "#F5F5F5", display: "flex", alignItems: "center", justifyContent: "center", color: "#0a0a0a", textDecoration: "none" }}>
                <PhoneIcon />
              </a>
            )}
            {venue.website && (
              <a href={venue.website.startsWith("http") ? venue.website : `https://${venue.website}`} target="_blank" rel="noopener noreferrer" title="Site web" style={{ width: 44, height: 44, borderRadius: "50%", border: "1px solid #E0E0E0", backgroundColor: "#F5F5F5", display: "flex", alignItems: "center", justifyContent: "center", color: "#0a0a0a", textDecoration: "none" }}>
                <GlobeIcon />
              </a>
            )}
            {venue.latitude && venue.longitude && (
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${venue.latitude},${venue.longitude}`} target="_blank" rel="noopener noreferrer" title="Itinéraire" style={{ width: 44, height: 44, borderRadius: "50%", border: "1px solid #E0E0E0", backgroundColor: "#F5F5F5", display: "flex", alignItems: "center", justifyContent: "center", color: "#0a0a0a", textDecoration: "none" }}>
                <DirectionsIcon />
              </a>
            )}
            {(venue.has_services || venue.has_availability) && !isOwner && (
              <button onClick={() => venue.has_services ? setShowServiceBookingModal(true) : setShowBookingModal(true)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 18px", height: 44, borderRadius: 22, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 13.5, fontWeight: 700, cursor: "pointer" }}>
                <CalendarIcon /> {t(lang, "venue_tab_book")}
              </button>
            )}
            {venue.has_provider_section && !isOwner && (
              <button onClick={() => navigate(`/venue/${id}/prestataires`)} title="Prestataires" style={{ width: 44, height: 44, borderRadius: "50%", border: "1px solid #E0E0E0", backgroundColor: "#F5F5F5", display: "flex", alignItems: "center", justifyContent: "center", color: "#0a0a0a", cursor: "pointer" }}>
                <BriefcaseIcon />
              </button>
            )}
            {/* Suivre/ne plus suivre — poussé à l'extrémité droite de la même rangée que les
                icônes appeler/site/itinéraire, comme demandé, plutôt qu'une rangée à part. */}
            {!isOwner && (
              <button
                onClick={venue.my_member_role != null ? () => setActiveTab("members") : toggleFollow}
                disabled={followLoading}
                style={{
                  marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, padding: "0 18px", height: 44, borderRadius: 22,
                  border: venue.followed_by_me || venue.my_member_role != null ? "1px solid #E0E0E0" : "none",
                  backgroundColor: venue.followed_by_me || venue.my_member_role != null ? "white" : "#0a0a0a",
                  color: venue.followed_by_me || venue.my_member_role != null ? "#0a0a0a" : "white",
                  fontSize: 13.5, fontWeight: 700, cursor: followLoading ? "default" : "pointer", opacity: followLoading ? 0.6 : 1,
                }}
              >
                {/* Membre l'emporte sur Abonné : un membre est automatiquement abonné (voir
                    activateMembership côté backend), le bouton doit refléter la relation la plus
                    forte des deux, pas juste la dernière calculée. */}
                {venue.my_member_role != null ? `✓ ${t(lang, "venue_btn_member")}` : venue.followed_by_me ? `✓ ${t(lang, "venue_btn_follower")}` : `+ ${t(lang, "venue_btn_follow")}`}
              </button>
            )}
          </div>

          {/* Gestion / revendication du lieu */}
          {venue.owner_id === user?.id ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, backgroundColor: "#F0F0F0", borderRadius: 20, padding: "6px 12px" }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#0a0a0a" }}>✓ Vous gérez ce lieu</span>
              </div>
              <button onClick={openEditModal} style={{ backgroundColor: "white", border: "1px solid #E0E0E0", borderRadius: 20, padding: "6px 14px", fontSize: 12, fontWeight: 600, color: "#0a0a0a", cursor: "pointer" }}>
                Modifier
              </button>
              <button onClick={() => navigate(`/create?venue_id=${id}&venue_name=${encodeURIComponent(venue.name)}`)} style={{ backgroundColor: "#0a0a0a", border: "none", borderRadius: 20, padding: "6px 14px", fontSize: 12, fontWeight: 600, color: "white", cursor: "pointer" }}>
                + {t(lang, "venue_add_publication")}
              </button>
              <button onClick={() => navigate(`/venue-agenda/${id}`)} style={{ display: "flex", alignItems: "center", gap: 6, backgroundColor: "white", border: "1px solid #E0E0E0", borderRadius: 20, padding: "6px 14px", fontSize: 12, fontWeight: 600, color: "#0a0a0a", cursor: "pointer" }}>
                <CalendarIcon size={14} /> {t(lang, "venue_manage_schedule")}
              </button>
              <button onClick={() => navigate(`/venue/${id}/prestataires`)} style={{ display: "flex", alignItems: "center", gap: 6, backgroundColor: "white", border: "1px solid #E0E0E0", borderRadius: 20, padding: "6px 14px", fontSize: 12, fontWeight: 600, color: "#0a0a0a", cursor: "pointer" }}>
                <BriefcaseIcon size={14} /> {t(lang, "venue_manage_service")}
              </button>
              <button onClick={() => setShowCreditPointsModal(true)} style={{ display: "flex", alignItems: "center", gap: 6, backgroundColor: "white", border: "1px solid #E0E0E0", borderRadius: 20, padding: "6px 14px", fontSize: 12, fontWeight: 600, color: "#0a0a0a", cursor: "pointer" }}>
                <StarIcon size={14} /> {t(lang, "venue_credit_points_btn")}
              </button>
              <button onClick={() => setShowSponsorModal(true)} style={{ backgroundColor: "white", border: "1px solid #E0E0E0", borderRadius: 20, padding: "6px 14px", fontSize: 12, fontWeight: 600, color: "#0a0a0a", cursor: "pointer" }}>
                {t(lang, "venue_boost")}
              </button>
            </div>
          ) : isVenueStaff ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 4 }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, backgroundColor: "#F0F0F0", borderRadius: 20, padding: "6px 12px" }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#0a0a0a" }}>✓ Vous administrez ce lieu</span>
              </div>
              <button onClick={() => navigate(`/create?venue_id=${id}&venue_name=${encodeURIComponent(venue.name)}`)} style={{ backgroundColor: "#0a0a0a", border: "none", borderRadius: 20, padding: "6px 14px", fontSize: 12, fontWeight: 600, color: "white", cursor: "pointer" }}>
                + {t(lang, "venue_add_publication")}
              </button>
            </div>
          ) : !venue.owner_id ? (
            venue.claim_pending ? (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, backgroundColor: "#F8F8F8", border: "1px solid #E0E0E0", borderRadius: 20, padding: "6px 12px", marginTop: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: "#999" }}>Demande de revendication en cours</span>
              </div>
            ) : (
              <button onClick={() => setShowClaimModal(true)} style={{ backgroundColor: "#F8F8F8", border: "1px solid #E0E0E0", borderRadius: 20, padding: "6px 14px", fontSize: 12, fontWeight: 600, color: "#0a0a0a", cursor: "pointer", marginTop: 4 }}>
                C'est mon établissement ? Revendiquer ce lieu
              </button>
            )
          ) : null}
        </div>

        {/* SOUVENIRS puis ABONNÉS — deux sections fixes avant les onglets (pas des tabs à part
            entière), dans cet ordre : Souvenirs passe en premier quand j'ai du contenu perso à
            ce lieu, sinon Abonnés reste la première chose vue en arrivant sur la fiche. */}
        {memories.length > 0 && (
          <div style={{ padding: "0 24px 20px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#0a0a0a", marginBottom: 10 }}>{t(lang, "venue_my_memories")}</div>
            <div className="no-scrollbar" style={{ display: "flex", gap: 10, overflowX: "auto" }}>
              {memories.map(m => (
                <a key={m.id} href={m.url} target="_blank" rel="noopener noreferrer" style={{ position: "relative", flexShrink: 0, width: 84, height: 84, borderRadius: 14, overflow: "hidden", backgroundColor: "#0a0a0a" }}>
                  {m.media_type === "video"
                    ? <video src={m.url} muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : <img src={m.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />}
                  {m.media_type === "video" && (
                    <span style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ width: 24, height: 24, borderRadius: "50%", backgroundColor: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="white"><polygon points="6,4 20,12 6,20" /></svg>
                      </span>
                    </span>
                  )}
                </a>
              ))}
            </div>
          </div>
        )}

        <div style={{ padding: "0 24px 20px" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0a0a0a", marginBottom: 10 }}>
            {Number(venue.followers_count || 0).toLocaleString()} {t(lang, "venue_followers_word")}{Number(venue.followers_count || 0) > 1 ? "s" : ""}
          </div>
          {followers.length > 0 ? (
            <div className="no-scrollbar" style={{ display: "flex", gap: 14, overflowX: "auto" }}>
              {followers.map(f => (
                <button key={f.id} onClick={() => navigate(`/profile/${f.id}`)} style={{ flexShrink: 0, width: 60, textAlign: "center", border: "none", backgroundColor: "transparent", cursor: "pointer" }}>
                  <div style={{ position: "relative", width: 48, height: 48, margin: "0 auto 5px" }}>
                    <div style={{ width: 48, height: 48, borderRadius: "50%", overflow: "hidden", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 13 }}>
                      {f.avatar_url ? <img src={f.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(f.name)}
                    </div>
                    {!!f.tier && <CrownBadge tier={f.tier} avatarSize={48} />}
                  </div>
                  <div style={{ fontSize: 10.5, color: "#666", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{(f.name || "").split(" ")[0]}</div>
                </button>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: "#999" }}>{t(lang, "venue_no_followers")}</div>
          )}
        </div>

        {/* TABS — défilement horizontal plutôt que flex:1 (qui écrasait chaque libellé quand un
            lieu Pro/Association ajoute "Membres"/"Pro Events"/"Gestion" aux 5 onglets de base :
            jusqu'à 8 boutons compressés dans la largeur de l'écran, texte tronqué/moche). */}
        <div className="no-scrollbar" style={{ display: "flex", overflowX: "auto", borderBottom: "1px solid #F0F0F0", backgroundColor: "white", position: "sticky", top: 0, zIndex: 10 }}>
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              flexShrink: 0, padding: "13px 14px", border: "none", cursor: "pointer",
              backgroundColor: "transparent", whiteSpace: "nowrap",
              borderBottom: activeTab === tab.key ? "2px solid #0a0a0a" : "2px solid transparent",
              color: activeTab === tab.key ? "#0a0a0a" : "#999",
              fontWeight: activeTab === tab.key ? 700 : 400, fontSize: 13,
            }}>{tab.label}</button>
          ))}
        </div>

        <div style={{ padding: "16px 24px" }}>

          {activeTab === "events" && (
            <div>
              {events.length === 0 && <div style={{ color: "#999", fontSize: 13, textAlign: "center", padding: 20 }}>{t(lang, "venue_no_upcoming_events")}</div>}
              {events.map(event => (
                <div key={event.id} style={{ ...card, padding: 0, overflow: "hidden" }}>
                  <div onClick={() => navigate(`/publication/${event.id}`)} style={{ height: 120, background: event.cover_url ? `url(${event.cover_url}) center/cover` : "linear-gradient(135deg, #1a1a1a, #0a0a0a)", position: "relative", padding: 12, cursor: "pointer" }}>
                    <div style={{ position: "absolute", top: 12, left: 12, backgroundColor: "rgba(255,255,255,0.2)", borderRadius: 20, padding: "3px 10px" }}>
                      <span style={{ color: "white", fontSize: 12, fontWeight: 600 }}>{(event.is_free || Number(event.price) === 0) ? t(lang, "venue_free") : `${event.price}€/pers`}</span>
                    </div>
                    {event.max_places && (
                      <div style={{ position: "absolute", top: 12, right: 12, backgroundColor: "rgba(0,0,0,0.4)", borderRadius: 20, padding: "3px 8px" }}>
                        <span style={{ color: "white", fontSize: 11 }}>{event.max_places - (event.current_places || 0)} {t(lang, "venue_places_suffix")}</span>
                      </div>
                    )}
                    <div style={{ position: "absolute", bottom: 12, left: 12 }}>
                      <div style={{ color: "white", fontWeight: 700, fontSize: 14 }}>{event.title}</div>
                      {event.event_date && <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }}>{new Date(event.event_date).toLocaleString(lang, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>}
                    </div>
                  </div>
                  <div style={{ padding: 14, display: "flex", justifyContent: "flex-end" }}>
                    <button onClick={() => participate(event.id)} style={{ padding: "8px 16px", borderRadius: 20, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{t(lang, "participate")}</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === "gallery" && (
            <div>
              {isOwner && (
                <label style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, border: "2px dashed #E0E0E0", borderRadius: 12, padding: 16, cursor: "pointer", marginBottom: 16, backgroundColor: "#F8F8F8" }}>
                  <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value = ""; }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#0a0a0a" }}>{uploadingPhoto ? "Envoi en cours..." : "+ Ajouter une photo (menu, salle, plats...)"}</span>
                </label>
              )}

              {photos.length === 0 && events.filter(e => e.cover_url).length === 0 && (
                <div style={{ color: "#999", fontSize: 13, textAlign: "center", padding: 20 }}>Aucune photo pour le moment</div>
              )}

              {photos.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4, marginBottom: events.filter(e => e.cover_url).length > 0 ? 16 : 0 }}>
                  {photos.map(p => (
                    <div key={p.id} style={{ position: "relative", aspectRatio: "1" }}>
                      <div style={{ width: "100%", height: "100%", backgroundImage: `url(${p.url})`, backgroundSize: "cover", backgroundPosition: "center", borderRadius: 8 }} />
                      {isOwner && (
                        <button onClick={() => deletePhoto(p.id)} style={{ position: "absolute", top: 4, right: 4, width: 22, height: 22, borderRadius: "50%", border: "none", backgroundColor: "rgba(0,0,0,0.6)", color: "white", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {events.filter(e => e.cover_url).length > 0 && (
                <>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#999", marginBottom: 8 }}>Photos des événements</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4 }}>
                    {events.filter(e => e.cover_url).map((e, i) => (
                      <div key={i} style={{ aspectRatio: "1", backgroundImage: `url(${e.cover_url})`, backgroundSize: "cover", backgroundPosition: "center", borderRadius: 8 }} />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === "catalogue" && (
            <div>
              {isOwner && (
                <button onClick={() => openItemModal()} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", border: "2px dashed #E0E0E0", borderRadius: 12, padding: 16, cursor: "pointer", marginBottom: 16, backgroundColor: "#F8F8F8" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#0a0a0a" }}>+ Ajouter un article</span>
                </button>
              )}

              {loadingItems && <div style={{ color: "#999", fontSize: 13, textAlign: "center", padding: 20 }}>{t(lang, "msg_loading")}</div>}

              {!loadingItems && items.length === 0 && (
                <div style={{ color: "#999", fontSize: 13, textAlign: "center", padding: 20 }}>{t(lang, "venue_no_items")}</div>
              )}

              {items.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)", gap: 10 }}>
                  {items.map(item => (
                    <div key={item.id} style={{ border: "1px solid #F0F0F0", borderRadius: 14, overflow: "hidden", backgroundColor: "white", opacity: item.is_available ? 1 : 0.55 }}>
                      <div style={{ aspectRatio: "1", backgroundColor: "#F5F5F5", backgroundImage: item.photo_url ? `url(${item.photo_url})` : "none", backgroundSize: "cover", backgroundPosition: "center", position: "relative" }}>
                        <span style={{ position: "absolute", top: 6, left: 6, backgroundColor: "rgba(0,0,0,0.65)", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 8, padding: "2px 7px" }}>
                          {item.item_type === "rental" ? t(lang, "venue_rental_label") : t(lang, "venue_sale_label")}
                        </span>
                        {!item.is_available && (
                          <span style={{ position: "absolute", top: 6, right: 6, backgroundColor: "rgba(192,57,43,0.85)", color: "white", fontSize: 10, fontWeight: 700, borderRadius: 8, padding: "2px 7px" }}>{t(lang, "venue_unavailable_suffix")}</span>
                        )}
                      </div>
                      <div style={{ padding: 10 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{item.title}</div>
                        {item.description && <div style={{ fontSize: 11.5, color: "#888", marginBottom: 4, lineHeight: 1.4 }}>{item.description}</div>}
                        {item.price != null && (
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#0a0a0a" }}>
                            {Number(item.price).toFixed(2)} € {item.item_type === "rental" ? "/ jour" : ""}
                            {item.points_price != null && <span style={{ fontWeight: 600, color: "#999" }}> · {t(lang, "catalog_or_points_suffix")} {item.points_price} pts</span>}
                          </div>
                        )}
                        {isOwner && (
                          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                            <button onClick={() => openItemModal(item)} style={{ fontSize: 11.5, fontWeight: 600, color: "#0a0a0a", background: "none", border: "none", cursor: "pointer", padding: 0 }}>Modifier</button>
                            <button onClick={() => toggleItemAvailable(item)} style={{ fontSize: 11.5, fontWeight: 600, color: "#666", background: "none", border: "none", cursor: "pointer", padding: 0 }}>{item.is_available ? "Marquer indispo" : "Marquer dispo"}</button>
                            <button onClick={() => deleteItem(item.id)} style={{ fontSize: 11.5, fontWeight: 600, color: "#c0392b", background: "none", border: "none", cursor: "pointer", padding: 0 }}>Suppr.</button>
                          </div>
                        )}
                        {!isOwner && item.is_available && item.price != null && Number(item.price) > 0 && (
                          <button
                            onClick={() => {
                              if (buyingItemId) return;
                              setBuyingItemId(item.id);
                              const success_url = `${window.location.origin}${window.location.pathname}?payment=success`;
                              const cancel_url = `${window.location.origin}${window.location.pathname}?payment=cancelled`;
                              api.post(`/venues/${id}/items/${item.id}/purchase`, { success_url, cancel_url, lang }).then(data => {
                                if (data?.checkoutUrl) window.location.href = data.checkoutUrl;
                                else if (data?.error) { alert(data.error); setBuyingItemId(null); }
                              }).catch(() => setBuyingItemId(null));
                            }}
                            disabled={buyingItemId === item.id}
                            style={{ width: "100%", marginTop: 8, padding: "8px", borderRadius: 10, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 12, fontWeight: 700, cursor: "pointer", opacity: buyingItemId === item.id ? 0.6 : 1 }}
                          >
                            {buyingItemId === item.id ? "..." : t(lang, "venue_buy_btn")}
                          </button>
                        )}
                        {!isOwner && item.is_available && item.points_price != null && Number(item.points_price) > 0 && myEngagement && myEngagement.points >= item.points_price && (
                          <button
                            onClick={() => {
                              if (buyingItemId) return;
                              setBuyingItemId(item.id);
                              // Jamais de Stripe/webhook ici (voir POST .../purchase-with-points côté
                              // serveur) : confirmation immédiate, on met juste à jour le solde affiché.
                              api.post(`/venues/${id}/items/${item.id}/purchase-with-points`, {}).then(data => {
                                if (data?.success) {
                                  setMyEngagement((prev: any) => prev ? { ...prev, points: data.remaining_points } : prev);
                                  alert(t(lang, "credit_points_success"));
                                } else if (data?.error) alert(data.error);
                                setBuyingItemId(null);
                              }).catch(() => setBuyingItemId(null));
                            }}
                            disabled={buyingItemId === item.id}
                            style={{ width: "100%", marginTop: 6, padding: "8px", borderRadius: 10, border: "1.5px solid #0a0a0a", backgroundColor: "white", color: "#0a0a0a", fontSize: 12, fontWeight: 700, cursor: "pointer", opacity: buyingItemId === item.id ? 0.6 : 1 }}
                          >
                            {buyingItemId === item.id ? "..." : `${t(lang, "catalog_pay_with_points_btn")} — ${item.points_price} pts`}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === "reviews" && (
            <div>
              {myReviewEditable ? (
                <div style={card}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>{myReview ? t(lang, "venue_edit_my_review") : t(lang, "venue_leave_review")} <span style={{ fontWeight: 400, color: "#999" }}>· {t(lang, "venue_review_scale_suffix")}</span></div>
                  {myReview && (
                    <div style={{ fontSize: 11, color: "#999", marginBottom: 10 }}>
                      {t(lang, "venue_review_editable_until_prefix")} {new Date(new Date(myReview.created_at).getTime() + REVIEW_EDIT_WINDOW_MS).toLocaleDateString(lang, { day: "numeric", month: "long" })}, {t(lang, "venue_review_editable_until_suffix")}
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 5, marginBottom: 10, flexWrap: "wrap" as const, marginTop: myReview ? 0 : 10 }}>
                    {[1,2,3,4,5,6,7,8,9,10].map(i => (
                      <button key={i} onClick={() => setMyRating(i)} style={{
                        width: 32, height: 32, borderRadius: 8,
                        border: `1px solid ${myRating === i ? "#0a0a0a" : "#E0E0E0"}`,
                        backgroundColor: myRating === i ? "#0a0a0a" : "#F8F8F8",
                        color: myRating === i ? "white" : "#666", fontSize: 12, fontWeight: 700, cursor: "pointer",
                      }}>{i}</button>
                    ))}
                  </div>
                  <textarea value={myComment} onChange={e => setMyComment(e.target.value)} placeholder={t(lang, "venue_comment_placeholder")} style={{ width: "100%", minHeight: 60, padding: 10, borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 13, outline: "none", resize: "vertical", marginBottom: 10 }} />
                  {reviewError && <div style={{ fontSize: 13, color: "#c0392b", marginBottom: 10 }}>{reviewError}</div>}
                  <button onClick={submitReview} disabled={myRating === 0 || submittingReview} style={{ padding: "9px 18px", borderRadius: 20, border: "none", backgroundColor: myRating === 0 ? "#E0E0E0" : "#0a0a0a", color: "white", fontSize: 13, fontWeight: 600, cursor: myRating === 0 ? "not-allowed" : "pointer" }}>
                    {submittingReview ? "..." : myReview ? t(lang, "venue_review_update_btn") : t(lang, "venue_review_publish_btn")}
                  </button>
                </div>
              ) : (
                <div style={card}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>{t(lang, "venue_my_review")} <span style={{ fontWeight: 400, color: "#999", fontSize: 12 }}>· {t(lang, "venue_review_definitive_suffix")}</span></div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: myReview.comment ? 8 : 0 }}>
                    <div style={{ backgroundColor: "#F0F0F0", borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 700, color: "#0a0a0a" }}>{formatScore(myReview.rating)}/10</div>
                    <span style={{ fontSize: 12, color: "#999" }}>{t(lang, "venue_review_published_prefix")} {new Date(myReview.created_at).toLocaleDateString(lang, { day: "numeric", month: "long", year: "numeric" })}</span>
                  </div>
                  {myReview.comment && <p style={{ fontSize: 13, color: "#555", lineHeight: 1.6, margin: 0 }}>{myReview.comment}</p>}
                </div>
              )}

              {reviews.length === 0 && (
                <div style={{ color: "#999", fontSize: 13, textAlign: "center", padding: 20 }}>{t(lang, "venue_no_reviews")}</div>
              )}
              {reviews.filter((r: any) => r.user_id !== user?.id).map(review => (
                <div key={review.id} style={card}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <div style={{ width: 40, height: 40, borderRadius: "50%", backgroundColor: colorFromId(review.user_id), display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, overflow: "hidden" }}>
                      {review.user_avatar ? <img src={review.user_avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(review.user_name)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{review.user_name}</div>
                      <div style={{ fontSize: 11, color: "#999" }}>{new Date(review.created_at).toLocaleDateString(lang, { month: "short", year: "numeric" })}</div>
                    </div>
                    <div style={{ backgroundColor: "#F0F0F0", borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 700, color: "#0a0a0a", flexShrink: 0 }}>{formatScore(review.rating)}/10</div>
                  </div>
                  {review.comment && <p style={{ fontSize: 13, color: "#555", lineHeight: 1.6, margin: 0 }}>{review.comment}</p>}
                </div>
              ))}
            </div>
          )}

          {activeTab === "infos" && (
            <div>
              {venue.description && (
                <div style={card}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{t(lang, "venue_about") || "À propos"}</div>
                  <p style={{ fontSize: 13, color: "#666", lineHeight: 1.7, margin: 0 }}>{venue.description}</p>
                </div>
              )}
              <div style={card}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{t(lang, "venue_info_section") || "Informations"}</div>
                {[
                  { label: t(lang, "venue_type") || "Type de lieu", value: venue.type },
                  { label: t(lang, "venue_category_label") || "Catégorie", value: venue.subtype === "pro" ? "Pro" : venue.subtype === "association" ? "Association" : null },
                  { label: t(lang, "venue_address") || "Adresse", value: venue.address },
                  { label: (t(lang, "venue_phone") || "Téléphone").replace(/\s*\(.*?\)/, ""), value: venue.phone },
                  { label: (t(lang, "venue_website") || "Site web").replace(/\s*\(.*?\)/, ""), value: venue.website },
                ].filter(row => row.value).map((row, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "10px 0", borderBottom: "1px solid #F5F5F5" }}>
                    <span style={{ fontSize: 12.5, color: "#999", flexShrink: 0 }}>{row.label}</span>
                    <span style={{ fontSize: 13, color: "#0a0a0a", fontWeight: 600, textAlign: "right" }}>{row.value}</span>
                  </div>
                ))}
              </div>

              {venue.hours && (
                <div style={card}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{t(lang, "venue_hours") || "Horaires"}</div>
                  {venue.hours.mode === "always_open" ? (
                    <div style={{ fontSize: 13, color: "#0a0a0a", fontWeight: 600 }}>{t(lang, "venue_hours_always_open") || "Ouvert tout le temps"}</div>
                  ) : (
                    DAYS_SHORT.map((label, dow) => {
                      const d = (venue.hours.schedule || []).find((s: any) => s.day === dow);
                      return (
                        <div key={dow} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #F8F8F8" }}>
                          <span style={{ fontSize: 13, color: "#555" }}>{label}</span>
                          <span style={{ fontSize: 13, color: !d || d.closed ? "#bbb" : "#0a0a0a", fontWeight: !d || d.closed ? 400 : 600 }}>
                            {!d || d.closed ? (t(lang, "venue_closed") || "Fermé") : `${d.open} – ${d.close}`}
                          </span>
                        </div>
                      );
                    })
                  )}
                  {(venue.hours.closures || []).filter((c: any) => c.start && c.end && c.end >= new Date().toISOString().slice(0, 10)).length > 0 && (
                    <div style={{ marginTop: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#999", marginBottom: 6 }}>{t(lang, "venue_upcoming_closures") || "Fermetures à venir"}</div>
                      {venue.hours.closures.filter((c: any) => c.start && c.end && c.end >= new Date().toISOString().slice(0, 10)).map((c: any, i: number) => (
                        <div key={i} style={{ fontSize: 12.5, color: "#c0392b", marginBottom: 4 }}>
                          {new Date(c.start).toLocaleDateString(lang, { day: "numeric", month: "short" })} – {new Date(c.end).toLocaleDateString(lang, { day: "numeric", month: "short" })}{c.reason ? ` · ${c.reason}` : ""}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === "clients" && isOwner && (
            <div>
              <p style={{ fontSize: 13, color: "#666", lineHeight: 1.6, marginBottom: 16 }}>
                Après chaque événement passé chez vous, notez les clients qui y ont participé. Leur évaluation compte dans leur score social OLTODO.
              </p>
              {loadingRateable && <div style={{ textAlign: "center", color: "#999", padding: 20 }}>Chargement…</div>}
              {!loadingRateable && rateableEvents.length === 0 && (
                <div style={{ textAlign: "center", color: "#999", fontSize: 13, padding: 20 }}>Aucun client à noter pour le moment.</div>
              )}
              {!loadingRateable && rateableEvents.map(ev => (
                <div key={ev.id} style={card}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>{ev.title}</div>
                  {ev.event_date && (
                    <div style={{ fontSize: 12, color: "#999", marginBottom: 14 }}>{new Date(ev.event_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</div>
                  )}
                  {ev.participants.map((p: any) => (
                    <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderTop: "1px solid #F5F5F5" }}>
                      <div style={{ width: 38, height: 38, borderRadius: "50%", backgroundColor: colorFromId(p.id), display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 12, overflow: "hidden", flexShrink: 0 }}>
                        {p.avatar_url ? <img src={p.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(p.name)}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{p.name}</span>
                      {p.already_rated ? (
                        <span style={{ fontSize: 12, color: "#999" }}>✓ Noté</span>
                      ) : (
                        <button onClick={() => { setRatingTarget({ eventId: ev.id, eventTitle: ev.title, client: p }); setRatingValues({}); }} style={{ backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 16, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Noter</button>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}

          {activeTab === "members" && venue.subtype !== "lieu" && (
            <div>
              {isVenueStaff && (
                <div style={card}>
                  <div onClick={() => setCheckinOpen(v => !v)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>Check-in QR</span>
                    <span style={{ fontSize: 12, color: "#999" }}>{checkinOpen ? "▲" : "▼"}</span>
                  </div>
                  {checkinOpen && (
                    <div style={{ textAlign: "center", marginTop: 14 }}>
                      <div style={{ fontSize: 12, color: "#666", marginBottom: 10 }}>Faites scanner ce code par vos membres à leur arrivée — il se renouvelle automatiquement.</div>
                      {checkinSvg ? (
                        <div style={{ display: "inline-block" }} dangerouslySetInnerHTML={{ __html: checkinSvg }} />
                      ) : (
                        <div style={{ width: 200, height: 200, margin: "0 auto", backgroundColor: "#F5F5F5", borderRadius: 12 }} />
                      )}
                    </div>
                  )}
                </div>
              )}

              {!isVenueMember && (
                <div style={{ ...card, textAlign: "center" }}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>{t(lang, "venue_join_members_prefix")} {venue.name}</div>
                  <div style={{ fontSize: 13, color: "#666", marginBottom: 14 }}>
                    {venue.join_policy === "invite_only" ? t(lang, "venue_join_invite_only") :
                     venue.join_policy === "open" ? t(lang, "venue_join_open") : t(lang, "venue_join_approval")}
                  </div>
                  {venue.my_member_status === "pending" ? (
                    <div style={{ fontSize: 13, color: "#999" }}>{t(lang, "venue_join_pending")}</div>
                  ) : venue.join_policy !== "invite_only" ? (
                    <button onClick={joinVenueMembers} disabled={memberActionLoading} style={{ backgroundColor: "#0a0a0a", color: "white", border: "none", borderRadius: 20, padding: "10px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                      {venue.join_policy === "open" ? t(lang, "venue_join_btn_open") : t(lang, "venue_join_btn_request")}
                    </button>
                  ) : null}
                </div>
              )}

              {isVenueMember && myEngagement && (
                <div style={card}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                    <div style={{ position: "relative", flexShrink: 0 }}>
                      <div style={{ width: 56, height: 56, borderRadius: "50%", backgroundColor: "#0a0a0a", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 17 }}>
                        {user?.avatar_url ? <img src={user.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(user?.name)}
                      </div>
                      {!!myEngagement.tier && <CrownBadge tier={myEngagement.tier} avatarSize={56} />}
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{t(lang, "venue_loyalty_title") || "Ma carte de fidélité"}</div>
                      {myEngagement.tier ? (
                        <span style={{ fontSize: 12, fontWeight: 800, borderRadius: 10, padding: "3px 9px", color: TIER_COLORS[myEngagement.tier as VenueTier], backgroundColor: `${TIER_COLORS[myEngagement.tier as VenueTier]}26` }}>
                          {TIER_LABELS[myEngagement.tier as VenueTier]}
                        </span>
                      ) : (
                        <span style={{ fontSize: 12, color: "#999" }}>{t(lang, "venue_loyalty_no_tier") || "Pas encore de palier"}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div style={{ flex: 1, textAlign: "center" }}>
                      <div style={{ fontSize: 24, fontWeight: 800 }}>{myEngagement.points}</div>
                      <div style={{ fontSize: 11.5, color: "#999", marginTop: 2 }}>{t(lang, "venue_loyalty_points") || "Points"}</div>
                    </div>
                    <div style={{ width: 1, height: 36, backgroundColor: "#E0E0E0" }} />
                    <div style={{ flex: 1, textAlign: "center" }}>
                      <div style={{ fontSize: 24, fontWeight: 800 }}>{myEngagement.visits_count}</div>
                      <div style={{ fontSize: 11.5, color: "#999", marginTop: 2 }}>{t(lang, "venue_loyalty_visits") || "Visites"}</div>
                    </div>
                    <div style={{ width: 1, height: 36, backgroundColor: "#E0E0E0" }} />
                    <div style={{ flex: 1, textAlign: "center" }}>
                      <div style={{ fontSize: 24, fontWeight: 800 }}>{myEngagement.events_attended_count}</div>
                      <div style={{ fontSize: 11.5, color: "#999", marginTop: 2 }}>{t(lang, "venue_loyalty_events") || "Événements"}</div>
                    </div>
                  </div>
                </div>
              )}

              {isVenueStaff && pendingMembers.length > 0 && (
                <div style={card}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Demandes en attente ({pendingMembers.length})</div>
                  {pendingMembers.map(m => (
                    <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #F5F5F5" }}>
                      <div style={{ width: 38, height: 38, borderRadius: "50%", backgroundColor: colorFromId(m.user_id), display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 12, overflow: "hidden", flexShrink: 0 }}>
                        {m.avatar_url ? <img src={m.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(m.name)}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{m.name}</span>
                      <button onClick={() => approveVenueMember(m.user_id)} style={{ fontSize: 12, padding: "6px 12px", borderRadius: 8, border: "none", backgroundColor: "#0a0a0a", color: "white", cursor: "pointer" }}>Accepter</button>
                      <button onClick={() => rejectVenueMember(m.user_id)} style={{ fontSize: 12, padding: "6px 12px", borderRadius: 8, border: "1px solid #E0E0E0", backgroundColor: "white", color: "#666", cursor: "pointer" }}>Refuser</button>
                    </div>
                  ))}
                </div>
              )}

              <div style={card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <span style={{ fontSize: 15, fontWeight: 700 }}>{Number(venue.members_count || 0).toLocaleString()} {t(lang, "venue_members_suffix")}</span>
                  <div style={{ display: "flex", gap: 8 }}>
                    {isVenueStaff && (
                      <button onClick={openInviteModal} style={{ fontSize: 12, padding: "6px 12px", borderRadius: 8, border: "none", backgroundColor: "#0a0a0a", color: "white", cursor: "pointer" }}>+ Inviter</button>
                    )}
                    {isVenueMember && !isVenueAdmin && (
                      <button onClick={leaveVenueMembers} style={{ fontSize: 12, padding: "6px 12px", borderRadius: 8, border: "1px solid #E0E0E0", backgroundColor: "white", color: "#666", cursor: "pointer" }}>{t(lang, "venue_leave_action")}</button>
                    )}
                  </div>
                </div>
                {venueMembers.map(m => {
                  const isSelf = m.user_id === user?.id;
                  return (
                    <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid #F5F5F5" }}>
                      <div onClick={() => navigate("/profile/" + m.user_id)} style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, cursor: "pointer", minWidth: 0 }}>
                        <div style={{ position: "relative", flexShrink: 0 }}>
                          <Avatar id={m.user_id} name={m.name} uri={m.avatar_url} size={44} ringKind={vibeRingKind(m.has_real, m.has_active)} />
                          {!!m.tier && <CrownBadge tier={m.tier} avatarSize={44} />}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                            <span style={{ fontSize: 14, fontWeight: 600 }}>{m.name}</span>
                            {m.role === "admin" && <span style={{ backgroundColor: "#F0F0F0", color: "#333", fontSize: 10, fontWeight: 600, borderRadius: 10, padding: "2px 7px" }}>{t(lang, "community_admin")}</span>}
                            {m.role === "staff" && <span style={{ backgroundColor: "#F0F0F0", color: "#666", fontSize: 10, fontWeight: 600, borderRadius: 10, padding: "2px 7px" }}>{t(lang, "venue_role_responsable")}</span>}
                          </div>
                          <div style={{ fontSize: 12, color: "#0a0a0a" }}>★ {formatScore(m.score)} / 10 · {t(lang, "venue_level_full")} {m.level} · {m.points} pts</div>
                        </div>
                      </div>
                      {isVenueAdmin && !isSelf && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                          <select value={m.role} onChange={e => changeVenueMemberRole(m.user_id, e.target.value)} style={{ fontSize: 12, padding: "6px 8px", borderRadius: 8, border: "1px solid #E0E0E0", backgroundColor: "white", color: "#555", cursor: "pointer" }}>
                            <option value="member">Membre</option>
                            <option value="staff">Responsable</option>
                            <option value="admin">Admin</option>
                          </select>
                          <button onClick={() => removeVenueMember(m.user_id, m.name)} style={{ fontSize: 12, padding: "6px 10px", borderRadius: 8, border: "1px solid #E0E0E0", backgroundColor: "#F8F8F8", color: "#666", cursor: "pointer" }}>Exclure</button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {venueMembers.length === 0 && <div style={{ textAlign: "center", color: "#999", fontSize: 13, padding: 20 }}>{t(lang, "venue_no_members")}</div>}
              </div>
            </div>
          )}

          {activeTab === "pro_events" && venue.subtype !== "lieu" && (
            <div>
              <div style={{ fontSize: 13, color: "#999", marginBottom: 16 }}>{t(lang, "venue_pro_events_intro")}</div>
              {(isVenueStaff || isOwner) && (
                <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                  <button onClick={() => navigate(`/create?venue_id=${id}&venue_name=${encodeURIComponent(venue.name)}&pro_event=1`)} style={{ backgroundColor: "#0a0a0a", border: "none", borderRadius: 20, padding: "8px 16px", fontSize: 12.5, fontWeight: 600, color: "white", cursor: "pointer" }}>
                    {t(lang, "venue_add_pro_event")}
                  </button>
                  <button onClick={() => navigate("/pro-events-network")} style={{ backgroundColor: "white", border: "1px solid #E0E0E0", borderRadius: 20, padding: "8px 16px", fontSize: 12.5, fontWeight: 600, color: "#0a0a0a", cursor: "pointer" }}>
                    {t(lang, "venue_pro_events_network_link")}
                  </button>
                </div>
              )}
              {proEvents === null ? (
                <div style={{ textAlign: "center", color: "#999", fontSize: 13, padding: 20 }}>Chargement…</div>
              ) : proEventsRestricted ? (
                <div style={{ textAlign: "center", color: "#999", fontSize: 13, padding: 20 }}>{t(lang, "venue_pro_events_restricted")}</div>
              ) : proEvents.length === 0 ? (
                <div style={{ textAlign: "center", color: "#999", fontSize: 13, padding: 20 }}>{t(lang, "venue_no_pro_events")}</div>
              ) : (
                proEvents.map(event => (
                  <div key={event.id} style={{ ...card, padding: 0, overflow: "hidden" }}>
                    <div onClick={() => navigate(`/publication/${event.id}`)} style={{ height: 120, background: event.cover_url ? `url(${event.cover_url}) center/cover` : "linear-gradient(135deg, #1a1a1a, #0a0a0a)", position: "relative", padding: 12, cursor: "pointer" }}>
                      <div style={{ position: "absolute", top: 12, left: 12, backgroundColor: "#9ACD32", borderRadius: 20, padding: "3px 10px" }}>
                        <span style={{ color: "#0a0a0a", fontSize: 12, fontWeight: 600 }}>{(event.is_free || Number(event.price) === 0) ? t(lang, "venue_free") : `${event.price}€/pers`}</span>
                      </div>
                      {event.max_places && (
                        <div style={{ position: "absolute", top: 12, right: 12, backgroundColor: "rgba(0,0,0,0.4)", borderRadius: 20, padding: "3px 8px" }}>
                          <span style={{ color: "white", fontSize: 11 }}>{event.max_places - (event.current_places || 0)} {t(lang, "venue_places_suffix")}</span>
                        </div>
                      )}
                      <div style={{ position: "absolute", bottom: 12, left: 12 }}>
                        <div style={{ color: "white", fontWeight: 700, fontSize: 14 }}>{event.title}</div>
                        {event.event_date && <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 12 }}>{new Date(event.event_date).toLocaleString(lang, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>}
                        {/* Co-organisation (voir POST /publications/:id/collaborators) : le lieu
                            organisateur si on regarde depuis la page d'un collaborateur, sinon les
                            collaborateurs acceptés si on regarde depuis la page de l'organisateur. */}
                        {event.is_collaboration && event.organizer_venue_name && (
                          <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t(lang, "pro_event_organized_by")} {event.organizer_venue_name}</div>
                        )}
                        {!event.is_collaboration && Array.isArray(event.collaborators) && event.collaborators.length > 0 && (
                          <div style={{ color: "rgba(255,255,255,0.8)", fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t(lang, "pro_event_co_organized_with")} {event.collaborators.map((c: any) => c.name).join(", ")}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === "ops" && isVenueStaff && (
            <div>
              {!selectedEventId ? (
                <div style={card}>
                  <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Événements dans ce lieu</div>
                  {venueEvents.map(ev => {
                    const remaining = ev.max_places ? ev.max_places - (ev.current_places || 0) : null;
                    return (
                      <div key={ev.id} onClick={() => setSelectedEventId(ev.id)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid #F5F5F5", cursor: "pointer" }}>
                        <div style={{ width: 44, height: 44, borderRadius: 10, backgroundColor: "#0a0a0a", backgroundImage: ev.cover_url ? `url(${ev.cover_url})` : "none", backgroundSize: "cover", backgroundPosition: "center", flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ev.title}</div>
                          <div style={{ fontSize: 11.5, color: "#999" }}>
                            {ev.event_date ? new Date(ev.event_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "Date libre"}
                            {remaining != null ? ` · ${remaining} places restantes` : ""}
                          </div>
                        </div>
                        <span style={{ fontSize: 12, color: "#0a0a0a", fontWeight: 600 }}>Voir →</span>
                      </div>
                    );
                  })}
                  {venueEvents.length === 0 && <div style={{ textAlign: "center", color: "#999", fontSize: 13, padding: 20 }}>Aucun événement dans ce lieu pour le moment.</div>}
                </div>
              ) : (
                <div>
                  <div onClick={() => setSelectedEventId(null)} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#0a0a0a", marginBottom: 14 }}>← Retour aux événements</div>
                  {loadingOps && <div style={{ textAlign: "center", color: "#999", padding: 20 }}>Chargement…</div>}
                  {!loadingOps && eventOps && (
                    <>
                      <div style={{ ...card, }}>
                        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>{eventOps.event.title}</div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 18 }}>
                          {[
                            { label: "Réservé", value: eventOps.summary.reserved },
                            { label: "Confirmé", value: eventOps.summary.confirmed },
                            { label: "Payé", value: eventOps.summary.paid },
                            { label: "Présent", value: eventOps.summary.attended },
                            { label: "Liste d'attente", value: eventOps.summary.waitlist },
                            { label: "Places restantes", value: eventOps.summary.places_remaining ?? "—" },
                          ].map(s => (
                            <div key={s.label}><div style={{ fontSize: 18, fontWeight: 800 }}>{s.value}</div><div style={{ fontSize: 11, color: "#999" }}>{s.label}</div></div>
                          ))}
                        </div>
                        <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #F0F0F0", display: "flex", gap: 24 }}>
                          <div><div style={{ fontSize: 18, fontWeight: 800 }}>{eventOps.summary.net_revenue.toFixed(2)} €</div><div style={{ fontSize: 11, color: "#999" }}>Revenu net</div></div>
                          <div><div style={{ fontSize: 18, fontWeight: 800 }}>{eventOps.summary.gross_revenue.toFixed(2)} €</div><div style={{ fontSize: 11, color: "#999" }}>Revenu brut</div></div>
                        </div>
                      </div>

                      <div style={card}>
                        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Participants ({eventOps.participants.length})</div>
                        {eventOps.participants.map((p: any) => (
                          <div key={p.user_id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #F5F5F5" }}>
                            <div style={{ width: 36, height: 36, borderRadius: "50%", backgroundColor: colorFromId(p.user_id), display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 11, overflow: "hidden", flexShrink: 0 }}>
                              {p.avatar_url ? <img src={p.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(p.name)}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                              <div style={{ fontSize: 11, color: "#999" }}>
                                {p.status === "confirmed" ? "Confirmé" : "En attente"}
                                {p.payment_status && ` · ${["succeeded", "releasing", "released"].includes(p.payment_status) ? "Payé" : p.payment_status}`}
                                {p.level != null && ` · Niv. ${p.level} (${p.points} pts sur ce lieu)`}
                              </div>
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 700, color: p.attended ? "#0a0a0a" : "#CCC" }}>{p.attended ? "✓ Présent" : "Non présent"}</span>
                          </div>
                        ))}
                        {eventOps.participants.length === 0 && <div style={{ textAlign: "center", color: "#999", fontSize: 13, padding: 20 }}>Aucun participant pour le moment.</div>}
                      </div>

                      {eventOps.waitlist.length > 0 && (
                        <div style={card}>
                          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Liste d'attente ({eventOps.waitlist.length})</div>
                          {eventOps.waitlist.map((w: any) => (
                            <div key={w.user_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid #F5F5F5" }}>
                              <div style={{ width: 30, height: 30, borderRadius: "50%", backgroundColor: colorFromId(w.user_id), display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 10, overflow: "hidden", flexShrink: 0 }}>
                                {w.avatar_url ? <img src={w.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(w.name)}
                              </div>
                              <span style={{ fontSize: 13, fontWeight: 600 }}>{w.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* SIDEBAR */}
      <div style={{ display: isMobile ? "none" : "block", width: 280, flexShrink: 0, borderLeft: "1px solid #F0F0F0", overflowY: "auto", padding: 20 }}>
        <div style={{ backgroundColor: "white", borderRadius: 16, padding: 16, border: "1px solid #F0F0F0" }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Résumé</div>
          {[
            { label: "Sorties actives", value: eventsCount.toLocaleString(), color: "#0a0a0a" },
            { label: "Avis clients", value: reviewsCount.toLocaleString(), color: "#0a0a0a" },
            { label: "Note moyenne", value: rating ? `${formatScore(rating)}/10` : "-", color: "#666666" },
          ].map((s, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #F8F8F8" }}>
              <span style={{ fontSize: 12, color: "#666" }}>{s.label}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: s.color }}>{s.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
          position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
          "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
          vrai viewport. */}
      {showInviteModal && ReactDOM.createPortal(
        <div onClick={() => setShowInviteModal(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "75vh", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <span style={{ fontSize: 17, fontWeight: 700 }}>Inviter des membres</span>
              <button onClick={() => setShowInviteModal(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>
            {loadingInviteFriends && <div style={{ fontSize: 13, color: "#999", padding: "8px 0" }}>Chargement de tes amis…</div>}
            {!loadingInviteFriends && inviteFriends.length === 0 && <div style={{ fontSize: 13, color: "#999", padding: "8px 0" }}>Tu n'as pas encore d'amis à inviter.</div>}
            {inviteFriends.map(f => {
              const alreadyMember = venueMembers.some(m => m.user_id === f.id) || pendingMembers.some(m => m.user_id === f.id);
              const invited = invitedIds.has(f.id);
              return (
                <div key={f.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #F5F5F5" }}>
                  <div style={{ width: 38, height: 38, borderRadius: "50%", backgroundColor: colorFromId(f.id), display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 12, overflow: "hidden", flexShrink: 0 }}>
                    {f.avatar_url ? <img src={f.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials(f.name)}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600, flex: 1 }}>{f.name}</span>
                  {alreadyMember ? (
                    <span style={{ fontSize: 12, color: "#999" }}>Déjà dans le lieu</span>
                  ) : (
                    <button onClick={() => inviteFriend(f.id)} disabled={invited} style={{ fontSize: 12, padding: "6px 14px", borderRadius: 16, border: "none", backgroundColor: invited ? "#F0F0F0" : "#0a0a0a", color: invited ? "#999" : "white", cursor: invited ? "default" : "pointer" }}>
                      {invited ? "✓ Invité" : "Inviter"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>,
        document.body
      )}

      {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
          position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
          "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
          vrai viewport. */}
      {showClaimModal && ReactDOM.createPortal(
        <div onClick={() => setShowClaimModal(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxWidth: 480 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <span style={{ fontSize: 17, fontWeight: 700 }}>Revendiquer {venue.name}</span>
              <button onClick={() => setShowClaimModal(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>
            {claimSent ? (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>Demande envoyée, elle sera examinée par notre équipe.</div>
              </div>
            ) : (
              <>
                <p style={{ fontSize: 13, color: "#666", lineHeight: 1.6, marginBottom: 14 }}>
                  Ce lieu a été ajouté par un autre utilisateur. Si c'est votre établissement, explique-nous brièvement comment le vérifier (numéro de téléphone professionnel, email, SIRET...), notre équipe validera ta demande avant de te transférer la gestion.
                </p>
                <textarea value={claimMessage} onChange={e => setClaimMessage(e.target.value)} placeholder="Ex: je suis le gérant, mon numéro pro est le 06..." style={{ width: "100%", minHeight: 90, padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 16, boxSizing: "border-box", resize: "none" as const }} />
                <button onClick={submitClaim} disabled={submittingClaim} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: submittingClaim ? 0.7 : 1 }}>
                  {submittingClaim ? "Envoi..." : "Envoyer la demande"}
                </button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Viewer des vibes/lives/reals de ce lieu — ouvert au tap sur la photo carrée quand elle
          porte un anneau. Composant partagé avec la bulle "lieu" de la barre Home. */}
      {vibesViewerOpen && (
        <VenueVibesViewer items={vibesItems} loading={loadingVibes} onClose={() => setVibesViewerOpen(false)} />
      )}

      {showSponsorModal && (
        <SponsorModal targetType="venue" targetId={id!} targetLabel={venue.name} onClose={() => setShowSponsorModal(false)} />
      )}

      {showCreditPointsModal && (
        <CreditPointsModal venueId={id!} venueSubtype={venue.subtype} lang={lang} onClose={() => setShowCreditPointsModal(false)} />
      )}

      {showBookingModal && (
        <BookingModal
          resource={{ type: "venue", id: id!, label: venue.name, category: selectedCategory || undefined }} providerName={venue.name}
          onClose={() => setShowBookingModal(false)}
          onBooked={() => {}}
        />
      )}

      {showServiceBookingModal && (
        <ServiceBookingModal
          venueId={id!} venueName={venue.name}
          averageRating={venue.average_rating} reviewsCount={venue.reviews_count}
          onClose={() => setShowServiceBookingModal(false)}
          onBooked={() => {}}
        />
      )}

      {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
          position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
          "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
          vrai viewport. */}
      {showItemModal && ReactDOM.createPortal(
        <div onClick={() => setShowItemModal(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 20, padding: 22, width: "100%", maxWidth: 420, maxHeight: "85vh", overflowY: "auto", boxSizing: "border-box" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
              <span style={{ fontSize: 17, fontWeight: 700 }}>{editingItem ? "Modifier l'article" : "Nouvel article"}</span>
              <button onClick={() => setShowItemModal(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>

            <label style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 120, borderRadius: 12, border: "2px dashed #E0E0E0", backgroundColor: "#F8F8F8", backgroundImage: itemPhoto ? `url(${itemPhoto})` : "none", backgroundSize: "cover", backgroundPosition: "center", cursor: "pointer", marginBottom: 14 }}>
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) uploadItemPhoto(f); e.target.value = ""; }} />
              {!itemPhoto && <span style={{ fontSize: 12.5, fontWeight: 600, color: "#0a0a0a" }}>{uploadingItemPhoto ? "Envoi..." : "+ Ajouter une photo"}</span>}
            </label>

            <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 6 }}>Titre</div>
            <input value={itemTitle} onChange={e => setItemTitle(e.target.value)} maxLength={150} placeholder="Ex : Vélo électrique" style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 12, boxSizing: "border-box" }} />

            <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 6 }}>Description</div>
            <textarea value={itemDescription} onChange={e => setItemDescription(e.target.value)} placeholder="Détails, état, conditions..." style={{ width: "100%", minHeight: 60, padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 12, boxSizing: "border-box", resize: "vertical" as const }} />

            <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 6 }}>Prix (€)</div>
                <input type="number" min="0" step="0.01" value={itemPrice} onChange={e => setItemPrice(e.target.value)} placeholder="0.00" style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 6 }}>Type</div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button onClick={() => setItemType("sale")} style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: itemType === "sale" ? "1.5px solid #0a0a0a" : "1px solid #E0E0E0", backgroundColor: itemType === "sale" ? "#0a0a0a" : "white", color: itemType === "sale" ? "white" : "#666", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>Vente</button>
                  <button onClick={() => setItemType("rental")} style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: itemType === "rental" ? "1.5px solid #0a0a0a" : "1px solid #E0E0E0", backgroundColor: itemType === "rental" ? "#0a0a0a" : "white", color: itemType === "rental" ? "white" : "#666", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>Location</button>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 6 }}>{t(lang, "catalog_points_price_label")}</div>
              <input type="number" min="0" step="1" value={itemPointsPrice} onChange={e => setItemPointsPrice(e.target.value.replace(/[^0-9]/g, ""))} placeholder="500" style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", boxSizing: "border-box" }} />
            </div>

            <button onClick={saveItem} disabled={!itemTitle.trim() || savingItem} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: (!itemTitle.trim() || savingItem) ? 0.5 : 1 }}>
              {savingItem ? "Enregistrement..." : editingItem ? "Mettre à jour" : "Ajouter au catalogue"}
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
          position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
          "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
          vrai viewport. */}
      {showEditModal && ReactDOM.createPortal(
        <div onClick={() => setShowEditModal(false)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "85vh", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <span style={{ fontSize: 17, fontWeight: 700 }}>Modifier {venue.name}</span>
              <button onClick={() => setShowEditModal(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>Nom</label>
            <input value={editName} onChange={e => setEditName(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 12, boxSizing: "border-box" }} />

            <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>Type de lieu</label>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              {VENUE_SUBTYPES.map(s => (
                <div
                  key={s.key}
                  onClick={() => setEditSubtype(s.key)}
                  style={{ flex: 1, textAlign: "center", padding: "10px 6px", borderRadius: 10, cursor: "pointer", border: `1.5px solid ${editSubtype === s.key ? "#0a0a0a" : "#E0E0E0"}`, backgroundColor: editSubtype === s.key ? "#0a0a0a" : "white" }}
                >
                  <div style={{ fontSize: 13, fontWeight: 700, color: editSubtype === s.key ? "white" : "#0a0a0a" }}>{s.label}</div>
                  {!!s.sub && <div style={{ fontSize: 10, color: editSubtype === s.key ? "rgba(255,255,255,0.7)" : "#999", marginTop: 2 }}>{s.sub}</div>}
                </div>
              ))}
            </div>
            {editSubtype !== "lieu" && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>Qui peut rejoindre vos Membres ?</label>
                {JOIN_POLICIES.map(p => (
                  <div key={p.key} onClick={() => setEditJoinPolicy(p.key)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 4px", cursor: "pointer" }}>
                    <div style={{ width: 16, height: 16, borderRadius: "50%", border: `1.5px solid ${editJoinPolicy === p.key ? "#0a0a0a" : "#CCC"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {editJoinPolicy === p.key && <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#0a0a0a" }} />}
                    </div>
                    <span style={{ fontSize: 13 }}>{p.label}</span>
                  </div>
                ))}
              </div>
            )}

            {editSubtype !== "lieu" && (
              <div style={{ fontSize: 12, color: "#999", backgroundColor: "#F7F7F7", borderRadius: 10, padding: "10px 12px", marginBottom: 12 }}>
                {t(lang, "billing_managed_in_settings") || "Identité de facturation gérée dans Réglages → Paiements"}
              </div>
            )}

            <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>Catégorie</label>
            <input value={editType} onChange={e => setEditType(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 12, boxSizing: "border-box" }} />

            <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>Description</label>
            <textarea value={editDescription} onChange={e => setEditDescription(e.target.value)} style={{ width: "100%", minHeight: 80, padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 12, boxSizing: "border-box", resize: "none" as const }} />

            <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>Téléphone</label>
            <input value={editPhone} onChange={e => setEditPhone(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 12, boxSizing: "border-box" }} />

            <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 }}>Site web</label>
            <input value={editWebsite} onChange={e => setEditWebsite(e.target.value)} style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, outline: "none", marginBottom: 16, boxSizing: "border-box" }} />

            <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 8 }}>Horaires d'ouverture</label>
            <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
              {[{ key: "scheduled", label: "Horaires précis" }, { key: "always_open", label: "Toujours ouvert" }].map(o => (
                <button key={o.key} onClick={() => setEditHours((prev: any) => ({ ...prev, mode: o.key }))} style={{ padding: "7px 13px", borderRadius: 16, fontSize: 12.5, fontWeight: 600, cursor: "pointer", border: editHours.mode === o.key ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: editHours.mode === o.key ? "#0a0a0a" : "white", color: editHours.mode === o.key ? "white" : "#666" }}>
                  {o.label}
                </button>
              ))}
            </div>

            {editHours.mode === "scheduled" && (
              <div style={{ marginBottom: 16 }}>
                {DAYS_SHORT.map((label, dow) => {
                  const d = dayHours(dow);
                  return (
                    <div key={dow} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}>
                      <span style={{ fontSize: 12.5, fontWeight: 600, width: 32, flexShrink: 0 }}>{label}</span>
                      <button onClick={() => setDayHours(dow, { closed: !d.closed })} style={{ fontSize: 11, fontWeight: 600, padding: "5px 10px", borderRadius: 12, border: "1px solid #E0E0E0", backgroundColor: d.closed ? "#F5F5F5" : "white", color: d.closed ? "#999" : "#0a0a0a", cursor: "pointer", flexShrink: 0 }}>
                        {d.closed ? "Fermé" : "Ouvert"}
                      </button>
                      {!d.closed && (
                        <>
                          <input type="time" value={d.open} onChange={e => setDayHours(dow, { open: e.target.value })} style={{ flex: 1, padding: "6px 8px", borderRadius: 8, border: "1px solid #E0E0E0", fontSize: 12.5, minWidth: 0 }} />
                          <input type="time" value={d.close} onChange={e => setDayHours(dow, { close: e.target.value })} style={{ flex: 1, padding: "6px 8px", borderRadius: 8, border: "1px solid #E0E0E0", fontSize: 12.5, minWidth: 0 }} />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <label style={{ fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 8 }}>Fermetures exceptionnelles</label>
            {(editHours.closures || []).map((c: any, i: number) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                <input type="date" min={new Date().toISOString().slice(0, 10)} value={c.start} onChange={e => updateClosure(i, { start: e.target.value })} style={{ flex: 1, padding: "7px 8px", borderRadius: 8, border: "1px solid #E0E0E0", fontSize: 12, minWidth: 0 }} />
                <input type="date" min={c.start || new Date().toISOString().slice(0, 10)} value={c.end} onChange={e => updateClosure(i, { end: e.target.value })} style={{ flex: 1, padding: "7px 8px", borderRadius: 8, border: "1px solid #E0E0E0", fontSize: 12, minWidth: 0 }} />
                <input value={c.reason} onChange={e => updateClosure(i, { reason: e.target.value })} placeholder="Raison" style={{ flex: 1, padding: "7px 8px", borderRadius: 8, border: "1px solid #E0E0E0", fontSize: 12, minWidth: 0 }} />
                <button onClick={() => removeClosure(i)} style={{ border: "none", background: "none", cursor: "pointer", color: "#c0392b", fontSize: 16, flexShrink: 0 }}>✕</button>
              </div>
            ))}
            <button onClick={addClosure} style={{ fontSize: 12.5, fontWeight: 600, color: "#0a0a0a", background: "none", border: "1px dashed #E0E0E0", borderRadius: 10, padding: "8px 12px", cursor: "pointer", marginBottom: 16, width: "100%" }}>
              + Ajouter une période de fermeture
            </button>

            <button onClick={saveEdit} disabled={savingEdit || !editName} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: savingEdit ? 0.7 : 1 }}>
              {savingEdit ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Portail direct sur document.body — même bug/fix que DatePromptSheet.tsx : un
          position:fixed descendant du conteneur mobile de Layout.tsx (WebkitOverflowScrolling:
          "touch") se positionne sur Safari iOS par rapport au contenu défilant plutôt qu'au
          vrai viewport. */}
      {ratingTarget && ReactDOM.createPortal(
        <div onClick={() => setRatingTarget(null)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "24px 24px 0 0", padding: 24, width: "100%", maxWidth: 480, maxHeight: "85vh", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 17, fontWeight: 700 }}>Noter {ratingTarget.client.name}</span>
              <button onClick={() => setRatingTarget(null)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#999" }}>✕</button>
            </div>
            <div style={{ fontSize: 12, color: "#999", marginBottom: 18 }}>{ratingTarget.eventTitle}</div>

            {RATING_CRITERIA.map(c => (
              <div key={c.key} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{c.label}</div>
                <div style={{ display: "flex", gap: 5 }}>
                  {[1,2,3,4,5,6,7,8,9,10].map(n => (
                    <button key={n} onClick={() => setRatingValues(prev => ({ ...prev, [c.key]: n }))} style={{
                      flex: 1, padding: "8px 0", borderRadius: 8,
                      border: `1px solid ${ratingValues[c.key] === n ? "#0a0a0a" : "#E0E0E0"}`,
                      backgroundColor: ratingValues[c.key] === n ? "#0a0a0a" : "#F8F8F8",
                      color: ratingValues[c.key] === n ? "white" : "#666",
                      fontSize: 11, fontWeight: ratingValues[c.key] === n ? 700 : 400, cursor: "pointer",
                    }}>{n}</button>
                  ))}
                </div>
              </div>
            ))}

            <button
              onClick={submitRating}
              disabled={submittingRating || !RATING_CRITERIA.every(c => ratingValues[c.key] > 0)}
              style={{
                width: "100%", padding: "13px", borderRadius: 12, border: "none",
                backgroundColor: RATING_CRITERIA.every(c => ratingValues[c.key] > 0) ? "#0a0a0a" : "#E0E0E0",
                color: "white", fontSize: 14, fontWeight: 600,
                cursor: RATING_CRITERIA.every(c => ratingValues[c.key] > 0) ? "pointer" : "not-allowed",
              }}
            >
              {submittingRating ? "Envoi..." : "Envoyer l'évaluation"}
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
    </>
  );
}
